import moment from "moment";
import JwtAxios, { BASE_URL } from "apiServices";
import {
  FABRIC_DEFECT_CATEGORY_LIST_ENDPOINT,
  FABRIC_INSPECTION_ADD_ENDPOINT,
  FABRIC_INSPECTION_DELETE_ENDPOINT,
  FABRIC_INSPECTION_EDIT_ENDPOINT,
  FABRIC_INSPECTION_EXPORT_ENDPOINT,
  FABRIC_INSPECTION_INVENTORY_DETAILS_ENDPOINT,
  FABRIC_INSPECTION_LIST_ENDPOINT,
  FABRIC_INSPECTION_SHOW_ENDPOINT,
  FABRIC_INSPECTION_STYLES_ENDPOINT,
} from "apiServices/API_ENDPOINTS";

/** Query param the styles endpoint filters on (FabricInspectionRepository::getStyleDetails). */
export const STYLE_SEARCH_PARAM = "style_no";

/** HTTP verb for update, matching `Route::put('fabric-inspections/edit/{id}')`. */
export const INSPECTION_UPDATE_METHOD = "put";

const DEFECT_CATEGORY_PAGE_SIZE = 100;

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export const isRequestCancelled = (error) =>
  error?.name === "CanceledError" || error?.code === "ERR_CANCELED";

/**
 * @param {string|string[]|undefined} message
 * @param {string} fallback
 * @returns {string}
 */
const toMessage = (message, fallback) => {
  if (Array.isArray(message)) return message.filter(Boolean).join(", ") || fallback;
  return typeof message === "string" && message ? message : fallback;
};

/**
 * Unwraps `{ code, status, message, data }`. Throws when the envelope reports a failure,
 * because the backend's invalidResponse() answers HTTP 200 with `code: 422`.
 * @param {import("axios").AxiosResponse} response
 * @param {string} fallback
 * @returns {unknown}
 */
const unwrap = (response, fallback) => {
  const body = response?.data;
  if (body?.code && body.code !== 200 && body.code !== 201) {
    throw new Error(toMessage(body.message, fallback));
  }
  return body?.data;
};

/**
 * @param {string} path
 * @param {Object} [params]
 * @param {AbortSignal} [signal]
 */
const get = (path, params, signal) => JwtAxios.get(`${BASE_URL}${path}`, { params, signal });

/**
 * @param {unknown} paginator
 * @returns {{rows: Array<unknown>, current:number, lastPage:number, pageSize:number, total:number}}
 */
const readPaginator = (paginator) => {
  const p = /** @type {Partial<import("components/FabricInspection/types").LaravelPaginator<unknown>>} */ (
    paginator || {}
  );
  return {
    rows: Array.isArray(p.data) ? p.data : [],
    current: Number(p.current_page) || 1,
    lastPage: Number(p.last_page) || 1,
    pageSize: Number(p.per_page) || 10,
    total: Number(p.total) || 0,
  };
};

/**
 * @param {{page:number, perPage:number, dateFrom?:string, dateTo?:string}} query
 * @param {AbortSignal} [signal]
 * @returns {Promise<import("components/FabricInspection/types").InspectionListResult>}
 */
export const fetchInspectionList = async ({ page, perPage, dateFrom, dateTo }, signal) => {
  const params = { page, per_page: perPage };
  // The list endpoint filters by inspection_date range; there is no month/year param.
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const response = await get(FABRIC_INSPECTION_LIST_ENDPOINT, params, signal);
  const paginated = readPaginator(unwrap(response, "Failed to load fabric inspections."));
  return {
    rows: /** @type {import("components/FabricInspection/types").InspectionRecord[]} */ (paginated.rows),
    pagination: { current: paginated.current, pageSize: paginated.pageSize, total: paginated.total },
  };
};

/**
 * @param {number} id
 * @param {AbortSignal} [signal]
 * @returns {Promise<import("components/FabricInspection/types").InspectionRecord>}
 */
export const fetchInspectionById = async (id, signal) => {
  const response = await get(`${FABRIC_INSPECTION_SHOW_ENDPOINT}/${id}`, undefined, signal);
  return /** @type {import("components/FabricInspection/types").InspectionRecord} */ (
    unwrap(response, "Failed to load fabric inspection.")
  );
};

/**
 * @param {{search?:string, page?:number, perPage?:number}} query
 * @param {AbortSignal} [signal]
 * @returns {Promise<import("components/FabricInspection/types").StylePage>}
 */
export const fetchStyles = async ({ search, page = 1, perPage = 20 }, signal) => {
  const params = { page, per_page: perPage };
  if (search) params[STYLE_SEARCH_PARAM] = search;

  const response = await get(FABRIC_INSPECTION_STYLES_ENDPOINT, params, signal);
  const paginated = readPaginator(unwrap(response, "Failed to load styles."));
  return {
    items: /** @type {import("components/FabricInspection/types").FabricStyle[]} */ (paginated.rows),
    currentPage: paginated.current,
    lastPage: paginated.lastPage,
  };
};

/**
 * Never called without both style and supplier.
 * @param {{styleId:number|null, supplierId:number|null, colorId?:number|null}} query
 * @param {AbortSignal} [signal]
 * @returns {Promise<import("components/FabricInspection/types").InventoryItem[]>}
 */
export const fetchInventoryDetails = async ({ styleId, supplierId, colorId }, signal) => {
  if (!styleId || !supplierId) return [];
  const params = { style_id: styleId, supplier_id: supplierId };
  if (colorId) params.color_id = colorId;

  const response = await get(FABRIC_INSPECTION_INVENTORY_DETAILS_ENDPOINT, params, signal);
  const data = unwrap(response, "Failed to load inventory details.");
  return Array.isArray(data) ? /** @type {import("components/FabricInspection/types").InventoryItem[]} */ (data) : [];
};

/**
 * Loads every page of defect categories and keeps only active ones.
 * Subcategories are also filtered by their parent id to guard against cross-category leaks.
 * @param {AbortSignal} [signal]
 * @returns {Promise<import("components/FabricInspection/types").DefectCategory[]>}
 */
export const fetchAllDefectCategories = async (signal) => {
  /** @type {import("components/FabricInspection/types").DefectCategory[]} */
  const all = [];
  let page = 1;
  let lastPage = 1;
  do {
    const response = await get(
      FABRIC_DEFECT_CATEGORY_LIST_ENDPOINT,
      { page, per_page: DEFECT_CATEGORY_PAGE_SIZE },
      signal,
    );
    const paginated = readPaginator(unwrap(response, "Failed to load defect categories."));
    const categories = /** @type {import("components/FabricInspection/types").DefectCategory[]} */ (paginated.rows);
    all.push(...categories);
    lastPage = paginated.lastPage;
    page += 1;
  } while (page <= lastPage);

  return all
    .filter((category) => category.status === "active")
    .map((category) => ({
      ...category,
      subcategories: (category.subcategories || [])
        .filter(
          (sub) => sub.status === "active" && Number(sub.defect_category_id) === Number(category.id),
        )
        .sort((a, b) => Number(a.name) - Number(b.name) || a.id - b.id),
    }))
    .filter((category) => category.subcategories.length > 0)
    .sort((a, b) => a.id - b.id);
};

/**
 * @param {unknown} error
 * @param {string} fallback
 * @returns {import("components/FabricInspection/types").SaveResult}
 */
const toSaveFailure = (error, fallback) => {
  const body = error?.response?.data;
  return {
    ok: false,
    message: toMessage(body?.message, error?.message || fallback),
    fieldErrors: body?.errors && typeof body.errors === "object" ? body.errors : {},
  };
};

/**
 * @param {import("axios").AxiosResponse} response
 * @param {string} fallback
 * @returns {import("components/FabricInspection/types").SaveResult}
 */
const toSaveResult = (response, fallback) => {
  const body = response?.data;
  if (body?.code === 200 || body?.code === 201) {
    return { ok: true, message: toMessage(body.message, "Saved successfully."), fieldErrors: {} };
  }
  return {
    ok: false,
    message: toMessage(body?.message, fallback),
    fieldErrors: body?.errors && typeof body.errors === "object" ? body.errors : {},
  };
};

/**
 * Uses JwtAxios directly (not postData) so Laravel 422 `errors` reach the form.
 * @param {import("components/FabricInspection/types").InspectionPayload} payload
 * @returns {Promise<import("components/FabricInspection/types").SaveResult>}
 */
export const createInspection = async (payload) => {
  const fallback = "Failed to create fabric inspection.";
  try {
    const response = await JwtAxios.post(`${BASE_URL}${FABRIC_INSPECTION_ADD_ENDPOINT}`, payload);
    return toSaveResult(response, fallback);
  } catch (error) {
    return toSaveFailure(error, fallback);
  }
};

/**
 * @param {number} id
 * @param {import("components/FabricInspection/types").InspectionPayload} payload
 * @returns {Promise<import("components/FabricInspection/types").SaveResult>}
 */
export const updateInspection = async (id, payload) => {
  const fallback = "Failed to update fabric inspection.";
  try {
    const response = await JwtAxios.request({
      method: INSPECTION_UPDATE_METHOD,
      url: `${BASE_URL}${FABRIC_INSPECTION_EDIT_ENDPOINT}/${id}`,
      data: payload,
    });
    return toSaveResult(response, fallback);
  } catch (error) {
    return toSaveFailure(error, fallback);
  }
};

/**
 * @param {number} id
 * @returns {Promise<import("components/FabricInspection/types").SaveResult>}
 */
export const deleteInspection = async (id) => {
  const fallback = "Failed to delete fabric inspection.";
  try {
    const response = await JwtAxios.delete(`${BASE_URL}${FABRIC_INSPECTION_DELETE_ENDPOINT}/${id}`);
    return toSaveResult(response, fallback);
  } catch (error) {
    return toSaveFailure(error, fallback);
  }
};

/**
 * Triggers an Excel file download from the backend export API.
 * @param {{dateFrom?: string, dateTo?: string, [key: string]: any}} [filters]
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
export const exportFabricInspections = async (filters = {}, signal) => {
  const params = {};
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  Object.keys(filters).forEach((key) => {
    if (key !== "dateFrom" && key !== "dateTo" && filters[key] !== undefined && filters[key] !== null) {
      params[key] = filters[key];
    }
  });

  try {
    const response = await JwtAxios.get(`${BASE_URL}${FABRIC_INSPECTION_EXPORT_ENDPOINT}`, {
      params,
      responseType: "blob",
      signal,
    });

    if (response.data?.type === "application/json") {
      const text = await response.data.text();
      const json = JSON.parse(text);
      return { ok: false, message: toMessage(json?.message, "Failed to export fabric inspections.") };
    }

    const disposition = response.headers?.["content-disposition"] || "";
    let fileName = `Fabric_Inspection_Report_${moment().format("YYYY-MM-DD")}.xlsx`;
    if (disposition && disposition.includes("filename=")) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        fileName = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { ok: true, message: "Fabric inspection report downloaded successfully." };
  } catch (error) {
    if (isRequestCancelled(error)) return { ok: false, message: "Export cancelled." };
    return { ok: false, message: error?.response?.data?.message || error?.message || "Failed to export fabric inspections." };
  }
};
